import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, ArrowUpRight, Search } from 'lucide-react';
import { Button, Card, CardHeader, Input, cn } from '../../components/ui';

/** Rows shown before "show all" — the long tail is rarely what's looked for. */
const INITIAL_ROWS = 25;

/** What every country row carries, whichever view built it. */
export interface BaseCountryRow {
  country: string | null;
  name: string;
  flag: string;
  /** Percentage of the view's primary count (devices, new devices, …). */
  share: number;
}

export interface CountryColumn<Row> {
  key: string;
  label: string;
  title: string;
  /** `null` sorts last whichever way the column is ordered. */
  sortValue: (row: Row) => number | null;
  /** [maxShare] is the largest row's share, for scaling a {@link ShareBar}. */
  render: (row: Row, maxShare: number) => ReactNode;
}

// ── Table ───────────────────────────────────────────────────────────────────

/**
 * Sortable, searchable country table: a table from `md` up and a card list
 * below it. Sorting and search run over rows already in memory — the server
 * is never asked again for a different order.
 */
export function CountryTable<Row extends BaseCountryRow>({
  title,
  description,
  rows,
  columns,
  linkFor,
  mobileValue,
  mobileSummary,
}: {
  title: string;
  description: string;
  rows: Row[];
  columns: CountryColumn<Row>[];
  /** The Users list a row opens; `undefined` leaves the row unlinked. */
  linkFor: (row: Row) => string | undefined;
  mobileValue: (row: Row) => ReactNode;
  mobileSummary: (row: Row) => ReactNode;
}) {
  const [sort, setSort] = useState({ key: columns[0]?.key ?? '', desc: true });
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? rows.filter(
          (row) =>
            row.name.toLowerCase().includes(needle) ||
            row.country?.toLowerCase() === needle,
        )
      : rows;
    const column = columns.find((c) => c.key === sort.key) ?? columns[0];
    if (!column) return filtered;
    const value = (row: Row) => column.sortValue(row) ?? -Infinity;
    // Stable on the incoming order, which each view already ranks.
    return [...filtered].sort((a, b) =>
      sort.desc ? value(b) - value(a) : value(a) - value(b),
    );
  }, [rows, columns, search, sort]);

  const shown = showAll || search ? visible : visible.slice(0, INITIAL_ROWS);
  const maxShare = useMemo(
    () => rows.reduce((max, row) => Math.max(max, row.share), 0),
    [rows],
  );

  const toggleSort = (key: string) =>
    setSort((current) =>
      current.key === key ? { key, desc: !current.desc } : { key, desc: true },
    );

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title={title}
        description={description}
        action={
          <div className="relative w-56">
            <Search
              size={14}
              className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-ink-3"
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Find a country"
              className="py-1.5 pl-8 text-sm"
            />
          </div>
        }
      />

      {rows.length === 0 ? (
        <p className="px-4 py-6 text-sm text-ink-3 sm:px-5">
          Nothing recorded in this period.
        </p>
      ) : shown.length === 0 ? (
        <p className="px-4 py-6 text-sm text-ink-3 sm:px-5">
          No country matches “{search}”.
        </p>
      ) : (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-sm">
              <thead className="bg-raised text-xs text-ink-2">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">Country</th>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className="px-3 py-2.5 text-right font-medium"
                      title={column.title}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort(column.key)}
                        className={cn(
                          'inline-flex items-center gap-1 whitespace-nowrap hover:text-ink',
                          sort.key === column.key && 'text-ink',
                        )}
                      >
                        {column.label}
                        {sort.key === column.key &&
                          (sort.desc ? (
                            <ArrowDown size={12} />
                          ) : (
                            <ArrowUp size={12} />
                          ))}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {shown.map((row) => (
                  <tr
                    key={row.country ?? 'unknown'}
                    className="transition-colors hover:bg-raised"
                  >
                    <td className="px-4 py-2.5">
                      <CountryName row={row} to={linkFor(row)} />
                    </td>
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className="tnum px-3 py-2.5 text-right text-ink-2"
                      >
                        {column.render(row, maxShare)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="divide-y divide-line md:hidden">
            {shown.map((row) => (
              <li key={row.country ?? 'unknown'} className="px-4 py-3">
                <div className="flex items-baseline justify-between gap-3">
                  <CountryName row={row} to={linkFor(row)} />
                  <span className="tnum text-sm font-semibold text-ink">
                    {mobileValue(row)}
                  </span>
                </div>
                <ShareBar share={row.share} max={maxShare} />
                <p className="mt-1.5 text-xs text-ink-3">{mobileSummary(row)}</p>
              </li>
            ))}
          </ul>
        </>
      )}

      {!search && visible.length > INITIAL_ROWS && (
        <div className="border-t border-line px-4 py-2.5 sm:px-5">
          <Button
            variant="ghost"
            className="text-xs"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll
              ? `Show top ${INITIAL_ROWS}`
              : `Show all ${visible.length.toLocaleString()} countries`}
          </Button>
        </div>
      )}
    </Card>
  );
}

function CountryName({ row, to }: { row: BaseCountryRow; to?: string }) {
  const label = (
    <>
      <span aria-hidden className="mr-1.5">
        {row.flag}
      </span>
      {row.name}
      {row.country && (
        <span className="ml-1.5 text-xs text-ink-3">{row.country}</span>
      )}
    </>
  );

  if (!to) return <span className="text-ink-2">{label}</span>;
  return (
    <Link to={to} className="font-medium text-ink hover:text-brand">
      {label}
    </Link>
  );
}

export function ShareBar({ share, max }: { share: number; max: number }) {
  return (
    <div className="mt-1 ml-auto h-1 w-full max-w-24 overflow-hidden rounded-full bg-raised md:w-24">
      <div
        className="h-full rounded-full bg-brand"
        // Scaled to the largest market so the tail is still distinguishable.
        style={{ width: `${Math.max((share / (max || 1)) * 100, 2)}%` }}
      />
    </div>
  );
}

export function GrowthValue({ value }: { value: number | null }) {
  return (
    <span
      className={cn(
        'tnum text-xs font-medium',
        value === null
          ? 'text-ink-3'
          : value > 0
            ? 'text-good'
            : value < 0
              ? 'text-danger'
              : 'text-ink-3',
      )}
    >
      {formatGrowth(value)}
    </span>
  );
}

// ── Tiles ───────────────────────────────────────────────────────────────────

export function InsightTile({
  icon,
  label,
  value,
  caption,
  to,
  tone = 'neutral',
}: {
  icon: ReactNode;
  label: string;
  value: string;
  caption?: ReactNode;
  /** The Users list filtered to what the tile is about. */
  to?: string;
  tone?: 'neutral' | 'brand';
}) {
  const body = (
    <>
      <div
        className={cn(
          'flex items-center gap-1.5 text-xs font-medium',
          tone === 'brand' ? 'text-brand' : 'text-ink-2',
        )}
      >
        {icon}
        <span className="truncate">{label}</span>
        {to && (
          <ArrowUpRight
            size={13}
            className="ml-auto shrink-0 opacity-0 transition-opacity group-hover:opacity-70 group-focus-visible:opacity-70"
          />
        )}
      </div>
      <p className="mt-2 truncate text-2xl font-semibold tracking-tight text-ink">
        {value}
      </p>
      {caption && <p className="mt-0.5 text-xs text-ink-3">{caption}</p>}
    </>
  );

  const className = cn(
    'group block rounded-xl border p-3.5 shadow-sm transition-colors sm:p-4',
    tone === 'brand' ? 'border-brand/25 bg-brand-soft' : 'border-line bg-surface',
    to &&
      (tone === 'brand'
        ? 'hover:border-brand/50'
        : 'hover:border-line-strong hover:bg-raised'),
  );

  return to ? (
    <Link to={to} className={className}>
      {body}
    </Link>
  ) : (
    <div className={className}>{body}</div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────────

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' });
  } catch {
    return null;
  }
})();

export function countryName(code: string | null): string {
  if (!code) return 'Unknown';
  try {
    return regionNames?.of(code) ?? code;
  } catch {
    // Not a region code `Intl` recognises — show what the device sent.
    return code;
  }
}

/** Regional-indicator pair for a two-letter code; a globe for anything else. */
export function flagFor(code: string | null): string {
  if (!code || !/^[A-Z]{2}$/.test(code)) return '🌐';
  return String.fromCodePoint(
    ...[...code].map((char) => 0x1f1e6 + char.charCodeAt(0) - 65),
  );
}

export function percent(part: number, whole: number): number {
  return whole > 0 ? (part / whole) * 100 : 0;
}

/** Change from [previous] to [current], `null` when there's nothing to compare. */
export function growth(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / previous) * 100;
}

export function formatPercent(value: number): string {
  return `${value < 10 && value > 0 ? value.toFixed(1) : Math.round(value)}%`;
}

export function formatGrowth(value: number | null): string {
  if (value === null) return 'new';
  const rounded = Math.round(value);
  return `${rounded > 0 ? '+' : ''}${rounded}%`;
}

export function maxBy<T>(
  items: T[],
  score: (item: T) => number,
): T | undefined {
  let best: T | undefined;
  let bestScore = -Infinity;
  for (const item of items) {
    const value = score(item);
    if (value > bestScore) {
      best = item;
      bestScore = value;
    }
  }
  return best;
}
